import React, { useState, useEffect, useRef } from 'react';
import _uniqueId from 'lodash/uniqueId';
import _merge from 'lodash/merge';
import { getContext, useCombinedRefs } from 'common';
import KEY_CODES from 'common/KeyCodes';
import List, { ListProps } from 'components/List';
import {
  TreeBranch,
  TreeIcon,
  TreeText,
  TreeInput,
  TreeTitle,
  TreeFooter,
  TreeContainer,
  TreeNode,
  TreeDropDownButton,
  TreeContextMenu,
  TreeFilterIcon,
  TreeFilterClearButton,
  TreeFilterContainer,
  TreeFilterMatchesContainer,
  TreeDragLine,
  TreeMoreButton,
  TreeMoreButtonItem,
  TreeMoreButtonItemText,
  TreeErrorMessage,
  TreeCheckbox,
} from 'components/Tree';
import { handleListKeyDown, handleListFocus } from './TreeKeyboardNav';
import Styled from './styles';

interface TreeContextInterface {
  truncateListText?: boolean;
  dndCallback?: any;
  dnd?: boolean;
  theme?: any;
  uniqueTreeId?: string;
  arrowKeyCallback?: any;
  keyboardNavigation?: boolean;
}

const TreeContext = React.createContext<TreeContextInterface | null>({});
const {
  ARROW_LEFT,
} = KEY_CODES;

const ELEVATION_INDEX = 0;
const ELEVATION_TRANSPARENT = true;

const TREE_LIST_STYLE = {
  minHeight: '2rem',
};

export interface TreeProps extends React.ComponentPropsWithRef<'div'> {
  /**  Items in the tree. Consider using `TreeBranch`, `TreeNode`, `TreeIcon`, `TreeText` */
  children: React.ReactNode;
  /** If true, the text in the tree items will be truncated with ellipsis if it exceeds the container width. Otherwise the Tree
    * will have a horizontal scroll bar.
    */
  truncateListText?: boolean;
  /** List properties. See List component documentation for details. */
  listProps?: Partial<ListProps>;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** If false, the default keyboard navigation is disabled. */
  keyboardNavigation?: boolean;
  /** If true, allow Tree component to handle the focus. Can set to true when disabling keyboardNavigation via the keyboardNavigation property. */
  keyboardFocus?: boolean;
  /** Enable/Disable component focus handler when keyboardNavigation = true. */
  preventFocusHandling?: boolean;
  /** If true, allow drag and drop of Tree items. */
  dnd?: boolean;
  /** Callback function fired when a tree item is dragged then dropped onto a target.
   *  - arguments: { draggedId, droppedOnToId }
   *  -  where draggedId is the `id` of the dragged treeitem
   *  -  droppedOnToId is the `id` of the treeitem for the drop
  */
  dndCallback?(...args: unknown[]): unknown;
  /** Number of Tree items that fit in one page. Used for keyboard navigation pageUp and pageDown. */
  itemsPerPage?: number;
  /** @ignore */
  onBlur?: React.FocusEventHandler;
  /** @ignore */
  onKeyDown?: React.KeyboardEventHandler;
}

/** The Tree component displays a hierarchy of items.
 * Any other props will be provided to the wrapper element.
*/
const Tree = React.forwardRef<HTMLElement, TreeProps>((props: TreeProps, ref) => getContext(({ theme }) => {
  const {
    children,
    onKeyDown,
    listProps,
    truncateListText,
    keyboardNavigation,
    keyboardFocus,
    preventFocusHandling,
    dnd,
    dndCallback,
    itemsPerPage,
    theme: propTheme,
    ...otherProps
  } = props;
  const {
    style: listStyle,
    ref: passedListRef,
    ...otherListProps
  } = listProps;
  const referenceElement = useRef(null);
  const combinedRef = useCombinedRefs(ref, referenceElement);
  const listRef = useRef(null);
  const combinedListRef = useCombinedRefs(passedListRef, listRef);
  const [wasTabPressed, setWasTabPressed] = useState(false);
  const [uniqueTreeId, setUniqueTreeId] = useState(undefined);
  useEffect(() => {
    setUniqueTreeId(_uniqueId('list-unique-identifier'));
  }, []); // on mount only

  // Callback function to keep track of when Tab was pressed. Use this to set
  // the tabIndex of the list box. If not, the list box will get focus when the
  // tab key is pressed while focused on any list items.
  const tabPressed = (val) => {
    setWasTabPressed(val);
  };

  // Need this function to reset the tabpressed state or the Tree will be left with tabIndex -1.
  const handleBlur = (event) => {
    setWasTabPressed(false);
    const { onBlur } = props;
    onBlur && onBlur(event);
  };

  // Callback function for handling ARROW_LEFT on the tree items (children).  ARROW_LEFT
  // takes the keyboard navigation back to the parent item of the treeitem. This function
  // is necessary to know when a child treeitem has had the ARROW_LEFT key pressed.
  const arrowKeyCallback = (event) => {
    const { key } = event;
    const keyDownFunction = handleListKeyDown({
      itemsPerPage,
      keyboardNavigation,
      onKeyDown
    }, combinedListRef, tabPressed, uniqueTreeId);
    key === ARROW_LEFT && keyDownFunction(event); // focus on Node parent
  };

  const resolvedListProps = _merge({}, {
    truncateListText: truncateListText ? true : undefined,
  }, otherListProps);
  const userTheme = propTheme && propTheme.TREE;
  const resolvedTheme = _merge({}, theme.TREE, userTheme);
  const resolvedContextProps = {
    uniqueTreeId,
    dndCallback,
    keyboardNavigation,
    dnd,
    arrowKeyCallback,
    truncateListText,
    theme: resolvedTheme
  };
  return (
    <Styled.TreeWrapper ref={combinedRef} role="tree" {...otherProps}>
      <List
        ref={combinedListRef}
        style={{
          ...TREE_LIST_STYLE,
          ...listStyle,
        }}
        useElevationStyle
        elevationProps={{
          elevationIndex: ELEVATION_INDEX,
          transparent: ELEVATION_TRANSPARENT,
        }}
        keyboardNavigation={false}
        onKeyDown={handleListKeyDown({
          itemsPerPage,
          keyboardNavigation,
          onKeyDown
        }, combinedListRef, tabPressed, uniqueTreeId)}
        onFocus={handleListFocus(props, combinedListRef, wasTabPressed, tabPressed, uniqueTreeId)}
        onBlur={handleBlur}
        tabIndex={wasTabPressed ? -1 : 0}
        {...resolvedListProps}
      >
        <TreeContext.Provider value={{ ...resolvedContextProps }}>
          {children}
        </TreeContext.Provider>
      </List>
    </Styled.TreeWrapper>
  );
}));

Tree.defaultProps = {
  truncateListText: false,
  listProps: {},
  theme: {},
  keyboardNavigation: true,
  keyboardFocus: false,
  preventFocusHandling: false,
  dnd: false,
  dndCallback: undefined,
  itemsPerPage: 0,
  onBlur: undefined,
  onKeyDown: undefined
};

Tree.displayName = 'Tree';

export {
  Tree as default,
  TreeBranch,
  TreeIcon,
  TreeText,
  TreeInput,
  TreeTitle,
  TreeNode,
  TreeDropDownButton,
  TreeContext,
  TreeContextMenu,
  TreeFilterIcon,
  TreeFilterClearButton,
  TreeFilterContainer,
  TreeFilterMatchesContainer,
  TreeDragLine,
  TreeMoreButton,
  TreeMoreButtonItem,
  TreeMoreButtonItemText,
  TreeErrorMessage,
  TreeFooter,
  TreeContainer,
  TreeCheckbox,
};
